// What the Studio shows for the open project (pen "Projects and Studio, two
// menu items", Decided 30 Oct): the project, the Studio's own frame while the
// projects load, or the empty view. The open project is only an id
// (`frame.studioProject`, kept across reloads); whether it is still there is
// read here against the loaded projects.
//
// Hostile links (SF-22): an id that does not exist, is not the author's, is
// archived or was deleted all read the same, `gone`, so the Studio never
// tells which. Pure.

export { hasTopBar, helpInOutline, studioOpenOf };

import { exhaustSwitchCase } from "@bilda/utils";
import type { StudioSurface } from "../../frame.js";
import type { ProjectItem } from "../../ports/project.js";

type StudioOpen =
    // No project open: the empty view.
    | { readonly type: "none" }
    // A project open, the projects not loaded yet: the Studio's own frame,
    // never "No project open" for a moment.
    | { readonly type: "loading" }
    | { readonly type: "open"; readonly project: ProjectItem }
    // The open project is no longer there to work on: the empty view with a
    // line saying so.
    | { readonly type: "gone" };

const studioOpenOf = (
    studioProject: string | null,
    projects: readonly ProjectItem[] | null,
): StudioOpen => {
    if (studioProject === null) return { type: "none" };
    if (projects === null) return { type: "loading" };
    const project = projects.find((p) => p.id === studioProject);
    if (project === undefined) return { type: "gone" };
    switch (project.lifecycle) {
        case "active":
            return { type: "open", project };
        case "archived":
            return { type: "gone" };
        default:
            return exhaustSwitchCase(project.lifecycle);
    }
};

// Whether the Studio shows its top bar (with the menu's toggle): on a
// project, or while one loads; the empty view has none.
const hasTopBar = (open: StudioOpen): boolean => {
    switch (open.type) {
        case "loading":
        case "open":
            return true;
        case "none":
        case "gone":
            return false;
        default:
            return exhaustSwitchCase(open);
    }
};

// Whether Help and feedback sits at the foot of the Studio's outline (pen
// Q76: never on the page) instead of on the page's bottom edge: wherever
// the Studio shows its pages, as `hasTopBar` decides, on the pages surface.
const helpInOutline = (open: StudioOpen, surface: StudioSurface): boolean => {
    switch (surface) {
        case "pages":
            return hasTopBar(open);
        case "classic":
            return false;
        default:
            return exhaustSwitchCase(surface);
    }
};
